/**
 * The article's assets, with the state a reader never sees (#1744 point 4).
 *
 * ── WHY THIS IS A DIFFERENT DOOR FROM THE PREVIEW'S ─────────────────────────
 * `KbArticlePreview` reads `service.kb_article_assets` — the READER's door,
 * which returns verified assets and no state, because to a reader there is
 * nothing else. This panel reads `service.kb_article_asset_states`, which takes
 * write authority and returns every live asset with its verdict.
 *
 * Two doors on one surface is not duplication: it is the surface showing an
 * author both of the things they need to hold in their head at once — what the
 * reader is getting, and what is still in the way.
 *
 * ── A QUARANTINE IS A PAIR OF NUMBERS, NEVER ONE ────────────────────────────
 * The server's model (`kb_asset_verdict_complete`, service/2026090111) is that
 * PENDING carries no verdict and VERIFIED and QUARANTINED both carry one. So a
 * quarantine is not "the server could not tell" — it is the server stating what
 * it actually found, and that disagreeing with the declaration.
 *
 * Rendering the declared size alone would say "48 kB, quarantined" and explain
 * nothing. Both numbers together explain everything: declared 48 kB, found 12
 * bytes. This panel therefore never shows one without the other on a
 * quarantined row.
 *
 * ── THE INSERT-FIGURE ACT LIVES IN THE EDITOR NOW, NOT HERE ───────────
 * This panel used to offer "copy `{{fig:CHEV-01}}` for the body" on every
 * verified figure, and its header argued the case honestly: a TRUE in-editor
 * insert meant extending `FieldEditor`, whose `slot` is bound to
 * `kind === 'ref'`, and widening that changes what every host renders for every
 * entity. Copying was the most a panel beside the form could do.
 *
 * #1679 settled it, and better than the widening I would have asked for:
 * `FieldEditor.actions` is a SEPARATE seam that hands an action the staged
 * value and the caret. So the real insert exists, in
 * `record/kbFigureAction.tsx`, and the copy button is GONE rather than left
 * standing beside it — two ways to do one thing, one of which silently drops
 * the author's caret, is worse than either alone.
 *
 * ── ATTACHING, AND WHY IT NEVER SAYS “DONE” ────────────────────────────────
 * This panel used to say, in its own note, that it did not upload — because
 * the console had no storage transport. It has one now (`@solum/sdk`'s
 * `putAuthenticatedObject`, ruled in mailbox 54560), so that note was retired
 * rather than left standing as a sentence the surface had made untrue.
 *
 * The act is `attachKbAsset`, and the whole of its shape is the bucket's rule:
 * row first, bytes second, at the key the SERVER minted. See `kbUpload.ts`.
 *
 * The thing this surface must get right is the SENTENCE AFTERWARDS. A
 * successful attach leaves the asset `pending` — no reader sees it, and
 * publication stays blocked, until the relay has read the bytes and agreed they
 * are what was declared. A panel that answered “Uploaded ✓” would be telling an
 * author their figure is in the article when it is not, and the row that
 * appears in the list beneath would then contradict the message above it. So
 * the success sentence names `pending` and says what is still true.
 *
 * ── AND THE KEY IS PREDICTABLE, BECAUSE THE AUTHOR HAS TO TYPE IT ──────────
 * The asset key is what goes inside `{{fig:…}}`. It is derived from the
 * filename (`assetKeyFromFilename`) so an author can predict it from the file
 * they just chose. A file whose name leaves nothing usable is refused with that
 * reason rather than given a generated id nobody could guess.
 */
import { useCallback, useState, type DragEvent, type ReactNode } from 'react'
import { Input, PageSection } from '@solum/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSolum, storageAccess } from '../data/client'
import { QueryBoundary, queryState } from '../data/QueryBoundary'
import { attachKbAsset, attachSaid, planAttach, type AttachSaid } from './kbUpload'
import s from './KbArticleAssets.module.css'
import { readableError } from '@solum/sdk'

/** Exactly what `service.kb_article_asset_states` returns. */
export interface KbAssetState {
  asset_id: string
  asset_key: string
  kind: string
  verification_state: string
  alt_text: string | null
  caption: string | null
  revision: number
  declared_content_type: string | null
  declared_size_bytes: number | string | null
  declared_sha256: string | null
  verified_content_type: string | null
  verified_size_bytes: number | string | null
  verified_sha256: string | null
  verified_at: string | null
  created_at: string | null
}

/** The server's three words, said once. Presentation only. */
const STATE_TEXT: Record<string, { label: string; tone: string; says: string }> = {
  verified: {
    label: 'Verified', tone: 'good',
    says: 'The server has read these bytes and they are what was declared. Readers see this figure.',
  },
  pending: {
    label: 'Pending', tone: 'wait',
    says: 'Uploaded, not yet checked. Readers do not see this figure and publication is blocked until it is verified.',
  },
  quarantined: {
    label: 'Quarantined', tone: 'bad',
    says: 'The server read these bytes and they are not what was declared. Readers will never see this figure.',
  },
}

/** Bytes, said the way a person reads them — and never rounded to nothing. */
function bytes(n: number | string | null): string {
  if (n == null) return '—'
  const v = Number(n)
  if (!Number.isFinite(v)) return '—'
  if (v < 1024) return `${v} B`
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} kB`
  return `${(v / 1024 / 1024).toFixed(1)} MB`
}

/* `AttachSaid` lives beside the act in `kbUpload.ts`, with the argument for why
   a successful attach is `wait` and not `good`. Re-exported so a host importing
   this panel does not need both modules to type its own state. */
export type { AttachSaid }

export interface AttachSurface {
  onFiles: (files: File[]) => void
  busy: boolean
  said: AttachSaid | null
}

export function KbArticleAssets({ id }: { id: string }): ReactNode {
  const { client } = useSolum()
  const raw = client.raw()
  const qc = useQueryClient()
  const [said, setSaid] = useState<AttachSaid | null>(null)
  const [busy, setBusy] = useState(false)
  const assets = useQuery({
    queryKey: ['kb', 'asset-states', id],
    queryFn: () => raw.rpcTable<KbAssetState>(
      'kb_article_asset_states', { p_article_id: id }, 'service'),
    /* A refusal is the honest answer for a colleague without write authority,
       and it must not be retried into a wall of failures. */
    retry: false,
  })

  const onFiles = useCallback((files: File[]) => {
    const plan = planAttach(files)
    if (!plan.go) {
      /* A refusal with no file at all is the browser's own no-op — a cancelled
         chooser — and must not overwrite what the region already says. */
      if (files.length > 0) setSaid(plan.said)
      return
    }
    setBusy(true)
    setSaid({ tone: 'wait', text: `Attaching ${files[0]!.name}…` })
    void attachKbAsset(client, storageAccess, {
      articleId: id, assetKey: plan.assetKey, kind: plan.kind, file: files[0]!,
    })
      .then((out) => {
        setSaid(attachSaid(out, plan.assetKey))
        /* The row the server just created must appear beneath the message that
           announced it — otherwise the panel says "attached and pending" over a
           list that does not contain it. */
        void qc.invalidateQueries({ queryKey: ['kb', 'asset-states', id] })
      })
      .catch((e: unknown) => {
        setSaid({ tone: 'bad', text: readableError(e, 'That file could not be attached.') })
      })
      .finally(() => { setBusy(false) })
  }, [client, id, qc])

  /* `queryState` returns an OBJECT and never null, so `if (state)` is ALWAYS
     true — the shape `KbArticleProcess` gets right and I got wrong in all three
     of these panels. Every one of them rendered an empty boundary for ever, and
     987 unit tests stayed green because they drive the PURE view directly.
     A browser found it in one navigation. Guard on `.kind`. */
  const gate = queryState([assets])
  if (gate.kind !== 'ready') {
    return (
      <QueryBoundary queries={[assets]} label="this article’s assets">
        {() => null}
      </QueryBoundary>
    )
  }
  return <KbAssetsView assets={assets.data ?? []} attach={{ onFiles, busy, said }} />
}

/** The pure half, so the property can be asserted with no client. */
export function KbAssetsView({ assets, attach }: {
  assets: KbAssetState[]
  /* Injected: the pure view holds no client and no token, and a host that
     cannot attach simply does not render the control rather than rendering one
     that fails at the wire. The clipboard prop that used to sit beside this is
     gone — the copy-marker fallback it fed was replaced by the real in-editor
     insert on #1679's accessory seam (`record/kbFigureAction.tsx`). */
  attach?: AttachSurface
}): ReactNode {
  if (assets.length === 0) {
    return (
      /* #2283 §3/§4 — the region's own heading names it, so the hand-written
         `aria-label` goes with the private title system rather than being
         carried forward: a landmark named by a word nobody can see is the
         second name for one region. */
      <PageSection title="Assets" headingLevel={3} testId="kb-assets">
        <div className={s.panel}>
        <p className={s.empty} data-testid="kb-assets-empty">
          This article has no figures or documents attached.
        </p>
        <AttachControl attach={attach} />
        <p className={s.note}>{VERIFIES}</p>
        </div>
      </PageSection>
    )
  }

  const blocking = assets.filter((a) => a.verification_state !== 'verified').length

  return (
    <PageSection title="Assets" headingLevel={3} testId="kb-assets">
      <div className={s.panel}>
      {/* The count first, because the one thing an author wants from this panel
          at a glance is whether anything is in the way. */}
      <p className={s.summary} data-testid="kb-assets-summary">
        {blocking === 0
          ? `All ${assets.length} ${assets.length === 1 ? 'asset is' : 'assets are'} verified.`
          : `${blocking} of ${assets.length} ${blocking === 1 ? 'asset is' : 'assets are'} not verified — readers cannot see ${blocking === 1 ? 'it' : 'them'}, and publication is blocked.`}
      </p>

      <ul className={s.list}>
        {assets.map((a) => {
          const st = STATE_TEXT[a.verification_state]
          const disagrees = a.verification_state === 'quarantined'
          return (
            <li key={a.asset_id} className={s.item} data-testid={`kb-asset-${a.asset_key}`}>
              <div className={s.itemHead}>
                <code className={s.key}>{a.asset_key}</code>
                {/* An unknown state renders its own code rather than vanishing:
                    a new verdict nobody spelled must be visible, not invisible. */}
                <span
                  className={s.state}
                  data-tone={st?.tone ?? 'unknown'}
                  data-testid={`kb-asset-state-${a.asset_key}`}
                >{st?.label ?? a.verification_state}</span>
                <span className={s.kind}>{a.kind}</span>
              </div>

              <p className={s.says} data-testid={`kb-asset-says-${a.asset_key}`}>
                {st?.says ?? `The server reports this asset as “${a.verification_state}”.`}
              </p>

              {/* THE DIAGNOSIS. Both numbers or neither — see the header. */}
              {disagrees && (
                <dl className={s.diff} data-testid={`kb-asset-diff-${a.asset_key}`}>
                  <div><dt>Declared</dt><dd>{bytes(a.declared_size_bytes)} · {a.declared_content_type ?? '—'}</dd></div>
                  <div><dt>Server found</dt><dd>{bytes(a.verified_size_bytes)} · {a.verified_content_type ?? '—'}</dd></div>
                </dl>
              )}

              {a.alt_text ? (
                <p className={s.alt}><span className={s.altLabel}>Alt text</span> {a.alt_text}</p>
              ) : (
                <p className={s.altMissing} data-testid={`kb-asset-noalt-${a.asset_key}`}>
                  No alt text — a reader using a screen reader gets nothing from this figure.
                </p>
              )}
            </li>
          )
        })}
      </ul>

      <AttachControl attach={attach} />
      <p className={s.note}>{VERIFIES}</p>
      </div>
    </PageSection>
  )
}

/**
 * The attach control — a drop target that is ALSO a real file input.
 *
 * Drag-and-drop alone is not a control: it cannot be reached from a keyboard,
 * it is invisible to a screen reader, and it does not exist on a touch device.
 * So the accessible name, the focus target and the activation are all the
 * `<input type="file">`; the drop handling is an ENHANCEMENT layered on the
 * same `onFiles`. The label wraps the input, which is why the whole zone
 * activates it — no `htmlFor`/`id` pair to drift, and no click handler
 * synthesising what a label already does natively.
 */
function AttachControl({ attach }: { attach?: AttachSurface }): ReactNode {
  const [over, setOver] = useState(false)
  if (!attach) return null
  const { onFiles, busy, said } = attach

  const drop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault()
    setOver(false)
    if (busy) return
    onFiles(Array.from(e.dataTransfer?.files ?? []))
  }

  return (
    <div className={s.attach} data-testid="kb-attach">
      <label
        className={s.drop}
        data-over={over || undefined}
        data-busy={busy || undefined}
        onDragOver={(e) => { e.preventDefault(); setOver(true) }}
        onDragLeave={() => { setOver(false) }}
        onDrop={drop}
      >
        {/* The SHARED control, not a raw `<input>`. `experience-adoption`'s
            `rawInput` census caught this on CI at 29 → 30, and it was right to:
            `Input` is a `forwardRef` over `<input>` taking any
            `InputHTMLAttributes`, so `type="file"` needs nothing it does not
            already have, and `cx(s.input, className)` keeps the clipping class
            below. There was something shared to adopt, so the baseline does not
            move. */}
        <Input
          className={s.file}
          type="file"
          accept="image/png,image/jpeg,image/webp,application/pdf"
          disabled={busy}
          data-testid="kb-attach-input"
          onChange={(e) => {
            onFiles(Array.from(e.target.files ?? []))
            /* Cleared so choosing the SAME file twice fires again — the second
               attempt after a refusal is the common one, and an input that
               stays set answers it with silence. */
            e.target.value = ''
          }}
        />
        <span className={s.dropText}>
          {busy ? 'Attaching…' : 'Drop a PNG, JPEG, WebP or PDF here, or choose a file'}
        </span>
      </label>
      {/* One live region, polite: the outcome arrives after the person has
          stopped interacting, so it must be announced rather than only shown. */}
      <p
        className={s.said}
        data-tone={said?.tone}
        role="status"
        aria-live="polite"
        data-testid="kb-attach-said"
      >{said?.text ?? ''}</p>
    </div>
  )
}

const VERIFIES =
  'The server reads every attached file and compares it against what was declared before any reader sees it. Until it agrees, the asset is pending and publication is blocked.'
